import { useCallback, useEffect, useRef, useState } from 'react';
import { addTestCases } from '../../../../modules/jira';
import { addTestSteps } from '../../../../modules/teststep';
import { addComment } from '../../../../modules/entity-details';
import { linkIssueToBulkEntities } from '../../../../modules/associations';
import { generateNotificationUrl } from '../../../../lib/notificationUtils';
import { AUTO_CLOSE_MS, SAVE_PHASE } from '../../test-cycles/static-data';
import { createWeblink } from '../../../../modules/traceability';
import { apiRoot } from "../../../../modules/callApi";

const toUuid = (val) => (val && val !== '' ? val : null);

const normalizeStep = ({ step, testData, expectedResult, uri }) => ({
  id: null,
  step: step || '',
  testData: testData || '',
  expectedResult: expectedResult || '',
  uri: uri || '',
});

const buildEntityId = (id, key, projectId) => ({
  entityId: id,
  entityKey: key,
  entityUrl: generateNotificationUrl('TestCase', projectId, key, 'traceability', null),
});

export const useBulkSave = ({
  jiraReviewTCs,
  setJiraReviewTCs,
  selectedStory,
  storyGroups = [],
  statuses,
  priorities,
  currentUserId,
  projectUsers,
  projectId,
  refetchData,
  handleClose,
  selectedExisting = new Set(),
  existingMatches = [],
  uploadedFileRef = null,
  confluenceLinkRef = null,
  onSaveSuccess,
}) => {
  const [savePhase, setSavePhase] = useState(SAVE_PHASE.IDLE);
  const [saveProgress, setSaveProgress] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  const autoCloseRef = useRef(null);
  const refetchDataRef = useRef(refetchData);
  const handleCloseRef = useRef(handleClose);
  const onSaveSuccessRef = useRef(onSaveSuccess);
  const savedKeysRef = useRef([]);

  useEffect(() => { refetchDataRef.current = refetchData; }, [refetchData]);
  useEffect(() => { handleCloseRef.current = handleClose; }, [handleClose]);
  useEffect(() => { onSaveSuccessRef.current = onSaveSuccess; }, [onSaveSuccess]);

  const resetSaveState = useCallback(() => {
    clearTimeout(autoCloseRef.current);
    setSavePhase(SAVE_PHASE.IDLE);
    setSaveProgress(null);
    setSaveResult(null);
    savedKeysRef.current = [];
  }, []);

  const resetSaveStateRef = useRef(resetSaveState);
  useEffect(() => { resetSaveStateRef.current = resetSaveState; }, [resetSaveState]);

  useEffect(() => {
    if (savePhase !== SAVE_PHASE.SUCCESS) return;
    const keys = savedKeysRef.current;
    if (onSaveSuccessRef.current) return;
    refetchDataRef.current?.(keys.length > 0 ? keys : undefined);
    autoCloseRef.current = setTimeout(() => {
      resetSaveStateRef.current();
      handleCloseRef.current?.();
    }, AUTO_CLOSE_MS);
    return () => clearTimeout(autoCloseRef.current);
  }, [savePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkSave = useCallback(async () => {
    const selected = jiraReviewTCs.filter((tc) => tc.selected);
    const hasNew = selected.length > 0;
    const hasExisting = selectedExisting.size > 0;
    if (!hasNew && !hasExisting) return;
    const uploadedFile = uploadedFileRef?.current;
    const confluenceLink = confluenceLinkRef?.current;

    const storySnapshot = selectedStory;
    const defaultStatus = statuses?.find((s) => s.is_default)?.id ?? null;
    const defaultPriority = priorities?.find((p) => p.is_default)?.id ?? null;
    const defaultOwner = currentUserId ?? projectUsers?.[0]?.id ?? null;

    if (!hasNew) {
      setSavePhase(SAVE_PHASE.LINKING);
      setSaveProgress(null);
    } else {
      setSavePhase(SAVE_PHASE.SAVING);
      setSaveProgress({ current: 0, total: selected.length, summary: '' });
    }

    const failed = [];
    // Each entry: { entityId, entityKey, entityUrl, _internalId, storyKey }
    const savedTestCases = [];

    for (let i = 0; i < selected.length; i++) {
      const tc = selected[i];
      setSaveProgress({ current: i + 1, total: selected.length, summary: tc.summary });
      try {
        const testcase = {
          summary: tc.summary,
          objective: tc.objective || '',
          preCondition: tc.preCondition || '',
          module: toUuid(tc.module),
          labels: tc.labels || [],
          technologyLayer: toUuid(tc.technologyLayer),
          testsuites: tc.testsuites || [],
          country: tc.country || [],
          priority: toUuid(tc.priority) ?? defaultPriority,
          status: toUuid(tc.status) ?? defaultStatus,
          owner: toUuid(tc.owner) ?? defaultOwner,
          reviewer: toUuid(tc.reviewer),
          automatedBy: toUuid(tc.automatedBy),
          automationMethod: tc.automationMethod || null,
          automationStatus: tc.automationStatus || null,
          isAutomated: tc.isAutomated ?? 'No',
          isAutomationNeeded: tc.isAutomationNeeded ?? false,
          reasonForNotAutomated: tc.reasonForNotAutomated || '',
          estimatedTime: tc.estimatedTime || '',
          automationCountry: tc.automationCountry || [],
          notAutomatedCategory: toUuid(tc.notAutomatedCategory),
          notAutomatedSubCategory: toUuid(tc.notAutomatedSubCategory),
        };

        const response = await addTestCases({ testcase }, projectId);
        const tcId = response?.test_case?.id;
        const tcKey = response?.test_case?.key;

        if (tc.testSteps?.length > 0) {
          try {
            await addTestSteps({ testSteps: tc.testSteps.map(normalizeStep) }, tcId);
          } catch (err) {
            console.error('Failed to save test steps for TC:', tc.summary, err);
          }
        }

        try {
          await addComment({
            comment: {
              body: 'This test case was generated by AI.',
              entityType: 'TestCase',
              entityId: tcId,
              mentions: [],
              additionalData: {},
            },
          }, projectId);
        } catch (err) {
          console.error('Failed to add AI comment for TC:', tcKey, err);
        }

        savedTestCases.push({
          ...buildEntityId(tcId, tcKey, projectId),
          _internalId: tc._id,
          storyKey: tc.storyKey ?? null,
        });
      } catch (error) {
        failed.push({ _id: tc._id, reason: error?.message || 'Unknown error' });
      }
    }

    const successCount = savedTestCases.length;

    // All new TCs failed — abort before linking
    if (hasNew && successCount === 0) {
      setSavePhase(SAVE_PHASE.ERROR);
      setSaveProgress(null);
      setSaveResult({ successCount: 0, failedCount: failed.length, linkError: false, storyKey: storySnapshot?.key });
      setJiraReviewTCs(
        jiraReviewTCs.map((tc) => {
          const f = failed.find((x) => x._id === tc._id);
          return f ? { ...tc, saveError: f.reason } : tc;
        }),
      );
      return;
    }

    // Proceed to linking phase
    setSavePhase(SAVE_PHASE.LINKING);
    setSaveProgress(null);

    let linkError = false;

    if (storySnapshot) {
      const existingEntityIds = [...selectedExisting].reduce((acc, id) => {
        const match = existingMatches.find((m) => m.id === id);
        if (match) acc.push(buildEntityId(match.id, match.key, projectId));
        return acc;
      }, []);

      const allEntityIds = [...savedTestCases, ...existingEntityIds];

      if (allEntityIds.length > 0) {
        try {
          await linkIssueToBulkEntities(
            {
              issue: {
                id: storySnapshot.id,
                key: storySnapshot.key,
                priority: storySnapshot.priority || '',
                projectId: storySnapshot.projectId,
              },
              entityIds: allEntityIds,
              entityType: 'TestCase',
              gainssProjectId: projectId,
            },
            projectId,
          );
        } catch (err) {
          console.error('Failed to link test cases to story:', storySnapshot?.key, err);
          linkError = true;
        }
      }
    } else if (storyGroups.length > 0) {
      for (const group of storyGroups) {
        const { story, tcInternalIds } = group;
        const groupEntityIds = savedTestCases.filter((tc) =>
          tcInternalIds.includes(tc._internalId),
        );
        if (!groupEntityIds.length) continue;
        try {
          await linkIssueToBulkEntities(
            {
              issue: {
                id: story.id,
                key: story.key,
                priority: story.priority || '',
                projectId: story.jiraProjectId,  
              },
              entityIds: groupEntityIds,
              entityType: 'TestCase',
              gainssProjectId: projectId,
            },
            projectId,
          );
        } catch (err) {
          console.error('Failed to link test cases to story:', story?.key, err);
          linkError = true;
        }
      }
    }

    setSaveResult({ successCount, failedCount: failed.length, linkError, storyKey: storySnapshot?.key });

    if (failed.length > 0) {
      setSavePhase(SAVE_PHASE.PARTIAL);
      refetchDataRef.current?.();
      setJiraReviewTCs(
        jiraReviewTCs.map((tc) => {
          const f = failed.find((x) => x._id === tc._id);
          return f ? { ...tc, saveError: f.reason } : { ...tc, selected: false };
        }),
      );
    } else {
      for (const tc of savedTestCases) {
        if (uploadedFile) {
          try {
            const formData = new FormData();
            formData.append('files', uploadedFile);
            formData.append('entityType', 'TestCase');
            formData.append('entityId', tc.entityId);
            await fetch(`${apiRoot}attachments?projectId=${projectId}`, {
              method: 'POST', body: formData,
              headers: { Authorization: localStorage.getItem('token') },
            });
          } catch (err) { console.error('Failed to upload attachment:', err); }
        }

        if (confluenceLink) {
          try {
            await createWeblink({
              weblink: {
                entityType: 'TestCase',
                entityId: tc.entityId,
                url: confluenceLink,
                description: 'Confluence link from Jira story',
              }
            });
          } catch (err) { console.error('Failed to save weblink:', err); }
        }
      }

        const savedKeys = savedTestCases.map((tc) => tc.entityKey).filter(Boolean);
        const savedIds  = savedTestCases.map((tc) => tc.entityId).filter(Boolean);
        savedKeysRef.current = savedKeys;
        setSavePhase(SAVE_PHASE.SUCCESS);
        onSaveSuccess?.({ savedKeys, savedIds });
  }
  }, [
    jiraReviewTCs, setJiraReviewTCs, selectedStory, storyGroups,
    statuses, priorities, currentUserId, projectUsers,
    projectId, selectedExisting, existingMatches,
  ]);

  return { savePhase, setSavePhase, saveProgress, saveResult, setSaveResult, handleBulkSave, resetSaveState };
};